import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/tr';
import { KTSVG } from 'src/_metronic/helpers';
import DefiniteRegistration from '../../applications/modals/definite-registration';
import PreRegistration from '../../applications/modals/pre-registration';

interface Event {
  id: string;
  title: string;
  start: string;
  type: 'definite' | 'presale';
  description?: string;
  display?: string;
  className?: string;
  backgroundColor?: string;
  allDay?: boolean;
  name?: string;
  area?: string;
  remainingPayment?: number;
  organisationFee?: number;
  phone?: string;
}

interface CalendarProps {
  events: Event[];
  onEventClick: (event: Event) => void;
  onDateSelect: (date: string, type: 'onGorusme' | 'kesinSatis') => void;
  presales?: any;
  organisation_typeList: any;
  areasList: any;
  onEventUpdate: any;
  onPresaleClick?: (date: string) => void;
  isFullscreen?: boolean;
}

const CustomCalendar: React.FC<CalendarProps> = ({
  events,
  onEventClick,
  onDateSelect,
  presales,
  areasList,
  organisation_typeList,
  onEventUpdate,
  onPresaleClick,
  isFullscreen,
}) => {
  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [showPreRegModal, setShowPreRegModal] = useState(false);
  const [showDefRegModal, setShowDefRegModal] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [showOverlay, setShowOverlay] = useState(false);

  // Ayın tüm günlerini oluştur
  const buildCalendarDays = () => {
    const start = currentDate.clone().startOf('month').startOf('week');
    const end = currentDate.clone().endOf('month').endOf('week');
    const days = [];

    let day = start.clone();
    while (day.isSameOrBefore(end)) {
      days.push(day.clone());
      day.add(1, 'day');
    }

    return days;
  };

  // Belirli bir tarih için etkinlikleri getir
  const getEventsForDate = (date: moment.Moment) => {
    return events.filter((event) => moment(event.start).format('YYYY-MM-DD') === date.format('YYYY-MM-DD'));
  };

  // Ön görüşme sayısını getir
  const getPresalesCount = (date: moment.Moment) => {
    if (!presales?.results) return 0;
    return presales.results.filter((presale: any) => moment(presale.date_of_organisation).format('YYYY-MM-DD') === date.format('YYYY-MM-DD')).length;
  };

  const handleDayClick = (day: moment.Moment, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipHeight = 120;
    const margin = 10;

    // fixed positioning: viewport-relative coordinates
    let yPos;
    if (rect.top - tooltipHeight - margin > 0) {
      yPos = rect.top - tooltipHeight - margin;
    } else {
      yPos = rect.bottom + margin;
    }

    const xPos = Math.min(Math.max(90, rect.left + rect.width / 2), window.innerWidth - 90);

    setTooltipPosition({
      x: xPos,
      y: yPos,
    });
    setSelectedDate(day.format('YYYY-MM-DD'));
    setShowOverlay(true);
  };

  const handleTooltipOptionClick = async (type: 'onGorusme' | 'kesinSatis') => {
    setTooltipPosition(null);
    setShowOverlay(false);

    // Tam ekrandaysa önce çık, sonra modalı aç
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    }

    if (type === 'onGorusme') {
      setShowPreRegModal(true);
    } else {
      setShowDefRegModal(true);
    }
  };

  const handleOverlayClick = () => {
    setTooltipPosition(null);
    setShowOverlay(false);
    setSelectedDate(null);
  };

  const handleModalClose = () => {
    setShowPreRegModal(false);
    setShowDefRegModal(false);
    setSelectedDate(null);
  };

  const handlePreRegSubmit = () => {
    if (selectedDate) {
      onDateSelect(selectedDate, 'onGorusme');
      handleModalClose();
    }
  };

  const handleDefRegSubmit = () => {
    if (selectedDate) {
      onDateSelect(selectedDate, 'kesinSatis');
      handleModalClose();
    }
  };

  const handlePresaleClick = (e: React.MouseEvent, date: moment.Moment) => {
    e.stopPropagation();
    // Yeni URL oluştur
    const baseUrl = window.location.origin;
    const searchParams = new URLSearchParams({
      page: '1',
      limit: '10',
      date_of_organisation: date.format('YYYY-MM-DD'),
      tabState: JSON.stringify({ key: 'pre' }),
    });

    const url = `${baseUrl}/applications/registration?${searchParams.toString()}`;

    // Yeni sekmede aç
    window.open(url, '_blank');
  };

  return (
    <div className="custom-calendar bg-white rounded-xl shadow-sm relative">
      <style>
        {`
          .custom-calendar {
            max-width: 100%;
            overflow: hidden;
          }
          
          .calendar-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1rem;
            background: #f5f8fa;
            border-bottom: 1px solid #eff2f5;
          }

          .calendar-grid {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 1px;
            background: #eff2f5;
            border: 1px solid #eff2f5;
          }

          .weekday-header {
            background: #ffffff;
            padding: 0.75rem;
            text-align: center;
            font-weight: 500;
            color: #5e6278;
          }

          .calendar-day {
            background: #ffffff;
            min-height: 100px;
            padding: 0.5rem;
            position: relative;
            transition: all 0.2s ease;
          }

          .calendar-day:hover {
            background: #f5f8fa;
          }

          .day-number {
            position: absolute;
            top: 0.5rem;
            right: 0.5rem;
            font-size: 0.875rem;
            color: #5e6278;
          }

          .current-day {
            font-weight: bold;
            color: #009ef7;
          }

          .different-month {
            color: #b5b5c3;
          }

          .selected-day {
            background: #f1faff !important;
            border: 1px solid #009ef7;
          }

          .event-indicator {
            margin-top: 1.5rem;
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .event-item {
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            font-size: 0.7rem;
            cursor: pointer;
            color: white;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.3;
          }

          .event-item .event-name {
            font-weight: 600;
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .event-item .event-detail {
            font-weight: 400;
            font-size: 0.65rem;
            opacity: 0.9;
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .event-definite {
            background: #f1416c;
          }

          .event-presale {
            background: #009ef7;
          }

          .presale-count {
            position: absolute;
            bottom: 0.2rem;
            left: 0.2rem;
            font-size: 0.75rem;
            color:rgb(93, 95, 94);
            font-weight: 500;
            background-color:rgb(230, 230, 230);
            padding: 2px 4px;
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s ease;
            border: 1px solid transparent;
          }

          .presale-count:hover {
            background-color: #D7F7E7;
            border-color: #20d489;
          }

          @media (max-width: 768px) {
            .calendar-day {
              min-height: 80px;
            }
            
            .event-item {
              font-size: 0.7rem;
              padding: 0.2rem 0.4rem;
            }
          }

          .custom-tooltip {
            position: fixed;
            background: white;
            border: 1px solid #ddd;
            border-radius: 8px;
            padding: 12px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            z-index: 1001;
            min-width: 180px;
            transform: translateX(-50%);
            pointer-events: all;
          }

          .tooltip-option {
            padding: 10px 16px;
            margin: 6px 0;
            cursor: pointer;
            border-radius: 6px;
            transition: all 0.2s ease;
            font-weight: 500;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }

          .on-gorusme {
            background-color: #3699FF;
          }

          .on-gorusme:hover {
            background-color: #1f7de6;
          }

          .kesin-satis {
            background-color: #f1416c;
          }

          .kesin-satis:hover {
            background-color: #d92952;
          }

          .overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 900;
            display: none;
          }

          .overlay.visible {
            display: block;
          }
        `}
      </style>

      <div className="calendar-header">
        <button className="btn btn-icon btn-light-primary" onClick={() => setCurrentDate((prev) => prev.clone().subtract(1, 'month'))}>
          <KTSVG path="/media/icons/duotone/Navigation/Angle-left.svg" className="svg-icon-2" />
        </button>

        <h2 className="text-lg font-medium">{currentDate.format('MMMM YYYY')}</h2>

        <button className="btn btn-icon btn-light-primary" onClick={() => setCurrentDate((prev) => prev.clone().add(1, 'month'))}>
          <KTSVG path="/media/icons/duotone/Navigation/Angle-right.svg" className="svg-icon-2" />
        </button>
      </div>

      <div className="calendar-grid">
        {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map((day) => (
          <div key={day} className="weekday-header">
            {day}
          </div>
        ))}

        {buildCalendarDays().map((day, index) => {
          const dateEvents = getEventsForDate(day);
          const presaleCount = getPresalesCount(day);
          const isCurrentDay = day.isSame(moment(), 'day');
          const isDifferentMonth = !day.isSame(currentDate, 'month');
          const isSelected = selectedDate === day.format('YYYY-MM-DD');

          return (
            <div key={index} className={`calendar-day ${isSelected ? 'selected-day' : ''}`} onClick={(e) => handleDayClick(day, e)}>
              <span className={`day-number ${isCurrentDay ? 'current-day' : ''} ${isDifferentMonth ? 'different-month' : ''}`}>{day.format('D')}</span>

              <div className="event-indicator">
                {dateEvents.map((event, eventIndex) => (
                  <div
                    key={eventIndex}
                    className={`event-item ${event.type === 'definite' ? 'event-definite' : 'event-presale'}`}
                    title={`${event.name || ''} - ${event.title} - ${event.area || ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                  >
                    <span className="event-name">{event.name}</span>
                    <span className="event-detail">
                      {event.title}
                      {event.area ? ` • ${event.area}` : ''}
                    </span>
                  </div>
                ))}
              </div>

              {presaleCount > 0 && (
                <div className="presale-count" onClick={(e) => handlePresaleClick(e, day)}>
                  {presaleCount} Aday
                </div>
              )}
            </div>
          );
        })}
      </div>

      {tooltipPosition && (
        <div
          className="custom-tooltip"
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`,
          }}
        >
          <div className="tooltip-option on-gorusme" onClick={() => handleTooltipOptionClick('onGorusme')}>
            Ön Görüşme Ekle
          </div>
          <div className="tooltip-option kesin-satis" onClick={() => handleTooltipOptionClick('kesinSatis')}>
            Kesin Satış Ekle
          </div>
        </div>
      )}

      <div className={`overlay ${showOverlay ? 'visible' : ''}`} onClick={handleOverlayClick}></div>

      {showPreRegModal && (
        <PreRegistration
          organisation_typeList={organisation_typeList || []}
          show={showPreRegModal}
          handleClose={setShowPreRegModal}
          initialDate={selectedDate}
          refetch={onEventUpdate}
          areasList={areasList || []}
        />
      )}

      {showDefRegModal && (
        <DefiniteRegistration
          show={showDefRegModal}
          handleClose={handleModalClose} // handleClose fonksiyonunu güncelledik
          actionData={selectedDate}
          setActionData={setSelectedDate}
          areasList={areasList || []}
          organisation_typeList={organisation_typeList || []}
          classesBranchList={[]}
          periodsList={[]}
          paymentList={[]}
          paymentInstallmentList={[]}
          isNew={isNew}
          setIsNew={setIsNew}
          refetch={onEventUpdate} // refetch fonksiyonunu da güncelliyoruz
        />
      )}
    </div>
  );
};

export default CustomCalendar;
